import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Paper,
  CircularProgress,
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
} from '@mui/material';
import { AppDatePicker } from '../../components/ui';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { tenantApi } from '../../api/tenantApi';
import { TenantCreatePayload } from '../../types/tenant';
import SchemaFormPanel from '../../components/SchemaForm/SchemaFormPanel';
import { tenantOptionSchema } from '../../config/tenantOptionSchema';
import { authConfigSchema } from '../../config/authConfigSchema';
import { useTenantAttrSchema } from '../../config/useTenantAttrSchema';
import axiosClient from '../../api/axiosClient';
import { createPollMissTracker, lostJobMessage } from '../../api/jobPollMisses';
import { useEnvOptional } from '../../context/EnvContext';
import { FILE_KEY_MAP } from '../../utils/zipParser';
import { safeUUID } from '../../utils/uuid';
import CreateCustomerDialog from './CreateCustomerDialog';
import CreateSponsorDialog from './CreateSponsorDialog';
import { useCurrentUser } from '../../auth/useCurrentUser';
import { colors } from '../../theme/tokens';
import { WriteButton } from '../../components/ui';

interface Customer { customer_id: string; customer_code: string; customer_name: string; customer_description: string; }
interface Sponsor { sponsor_id: string; customer_id: string; sponsor_code: string; sponsor_name: string; sponsor_description: string; }

// Shape of an export ZIP after parseExportZip(). Each key matches a file
// from FILE_KEY_MAP; payloads are dynamically validated downstream so we
// keep them as opaque records rather than typing every field.
type ImportFilePayload = Record<string, unknown> | unknown[];
interface ParsedImportData {
  tenant?: ImportFilePayload;
  task?: ImportFilePayload;
  cohort?: ImportFilePayload;
  cms?: ImportFilePayload;
  fis?: ImportFilePayload;
  sweepstakes?: ImportFilePayload;
  adventure?: ImportFilePayload;
  taskrewardcollection?: ImportFilePayload;
  wallet?: ImportFilePayload;
  admin?: ImportFilePayload;
  metadata?: ImportFilePayload;
  content?: ImportFilePayload;
  color?: ImportFilePayload;
  taskv2?: ImportFilePayload;
  lifecycle?: ImportFilePayload;
  flexapp?: ImportFilePayload;
  tenantnotification?: ImportFilePayload;
}

const HTML_TEMPLATES_LABEL = 'HTML Templates';

const MANUAL_STEPS = [
  { label: 'Basic Info', description: 'Tenant identity & core settings' },
  { label: 'Attributes', description: 'Feature flags, UX colors, config' },
  { label: 'Options', description: 'App menus, benefits, subscriptions' },
  { label: 'Auth Config', description: 'Auth0 & authentication settings' },
  { label: HTML_TEMPLATES_LABEL, description: 'Select CMS HTML templates' },
];

const IMPORT_STEPS = [
  { label: 'Upload', description: 'Import from ZIP or JSON files' },
  { label: 'Basic Info', description: 'Review identity & core settings' },
  { label: 'Attributes', description: 'Review feature flags & config' },
  { label: 'Options', description: 'Review app menus & subscriptions' },
  { label: 'Auth Config', description: 'Review authentication settings' },
  { label: HTML_TEMPLATES_LABEL, description: 'Select CMS HTML templates' },
];

type WizardMode = 'choose' | 'manual' | 'import';

const GoldConnector = styled(StepConnector)(() => ({
  '& .MuiStepConnector-line': { borderColor: colors.borderLight, borderTopWidth: 2 },
  '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': { borderColor: colors.brand },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (tenantCode: string) => void;
  initialCustomerCode?: string;
  initialSponsorCode?: string;
}

const freshForm = (versionedBy: string): TenantCreatePayload => ({
  tenant_code: `ten-${safeUUID().replace(/-/g, '')}`,
  partner_code: `par-${safeUUID().replace(/-/g, '')}`,
  api_key: safeUUID().replace(/-/g, ''),
  tenant_name: '',
  plan_year: new Date().getFullYear(),
  self_report: false,
  direct_login: false,
  enable_server_login: false,
  versioned_by: versionedBy,
});

const CreateTenantWizard: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  initialCustomerCode,
  initialSponsorCode,
}) => {
  const queryClient = useQueryClient();
  // Code schema + portal-registered definitions (mig 280), so an attribute added
  // from MasterTenant is also settable at tenant-creation time.
  const { schema: attrSchema, categories: attrCategories } = useTenantAttrSchema();
  const { email, displayName } = useCurrentUser();
  // Audit identity for create/import/publish calls. Falls back to a
  // labelled string when /api/me hasn't resolved yet (auth-disabled local
  // dev) so server-side audit columns never end up with literal 'admin'.
  const auditUser = email || displayName || 'wizard';

  const [mode, setMode] = useState<WizardMode>('choose');
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<TenantCreatePayload>(() => freshForm(auditUser));
  const [tenantAttr, setTenantAttr] = useState<Record<string, unknown>>({});
  const [tenantOption, setTenantOption] = useState<Record<string, unknown>>({});
  const [authConfig, setAuthConfig] = useState<Record<string, unknown>>({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<Record<string, string> | null>(null);
  const [importedFiles, setImportedFiles] = useState<Record<string, string>>({});
  const [parsedImportData, setParsedImportData] = useState<ParsedImportData>({});
  // Where the shown tenant code came from (drives the helper text under the
  // read-only Tenant Code field). 'generated' = fresh auto-generated (default /
  // manual / no source code / lookup error); 'zip' = reused the ZIP's source
  // code because it's free on this platform; 'taken' = generated because the
  // ZIP's source code is already on this platform. See applyZipTenantCode.
  const [tenantCodeSource, setTenantCodeSource] = useState<'generated' | 'zip' | 'taken'>('generated');
  const [tenantNameError, setTenantNameError] = useState<string | null>(null);
  const [checkingTenantName, setCheckingTenantName] = useState(false);
  // Content import always creates new Storyblok stories for the new tenant. The
  // one exception the server honours: this tenant has the SAME code as the ZIP's
  // (tenantCodeSource === 'zip', a cross-environment copy of the same tenant) and
  // the box below is unchecked, so the tenant's own existing stories are reused.
  // A ZIP from a different tenant is always created fresh, whatever this says.
  const [createContentStories, setCreateContentStories] = useState(true);
  const individualInputRef = React.useRef<HTMLInputElement>(null);
  const individualFileKey = React.useRef<string>('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({ open: false, severity: 'success', message: '' });
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedSponsor, setSelectedSponsor] = useState<Sponsor | null>(null);
  // customerCode/sponsorCode from the ZIP's tenant.json (see getTenantExport) —
  // populateFormFromTenant only copied sponsorId onto the form, so the Customer/
  // Sponsor pickers and the read-only Sponsor Code/Customer Code fields never
  // got auto-filled on import even though the ZIP carries both codes.
  const [zipCustomerCode, setZipCustomerCode] = useState<string | null>(null);
  const [zipSponsorCode, setZipSponsorCode] = useState<string | null>(null);
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [newSponsorOpen, setNewSponsorOpen] = useState(false);
  const [defaultTemplates, setDefaultTemplates] = useState<Array<{ key: string; language: string; fileName: string }>>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // Deployed env (dev|qa|integ|uat|prod) — drives which CMS bucket the default
  // HTML templates are read from and copied into (server resolves via getCmsBucket).
  // CPT-1137: pass the SELECTED env — html-template requests route
  // browser-direct to that env's pod, which resolves its own S3 bucket
  // server-side.
  const htmlTemplatesEnv = useEnvOptional()?.currentEnv ?? 'dev01';
  // Populated after a successful import; renders the summary screen in
  // place of the wizard steps until the user clicks "Go to Dashboard".
  // Live progress line while the async content-import job runs (pages/
  // categories/items created so far vs total) — replaces the opaque spinner.
  const [contentProgress, setContentProgress] = useState<string | null>(null);
  const [createdSummary, setCreatedSummary] = useState<{
    tenantCode: string;
    entityCounts: Record<string, number>;
    errors: string[];
    warnings: string[];
    danglingGlobalRefs: Array<{ kind: string; code: string; qualifier: string | null }>;
    status: string;
    publish?: {
      status?: string;
      entityCounts?: Record<string, number>;
      error?: string;
    } | null;
    content?: {
      status?: string;
      counts?: Record<string, number>;
      errors?: string[];
      warnings?: string[];
      linkedCohorts?: string[];
    } | null;
  } | null>(null);

  // Fetch customers
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['lookup-customers'],
    queryFn: async () => { const { data } = await axiosClient.get('/api/lookup/customers'); return data; },
    enabled: open,
  });

  // Fetch sponsors filtered by selected customer
  const { data: sponsors = [] } = useQuery<Sponsor[]>({
    queryKey: ['lookup-sponsors', selectedCustomer?.customer_id],
    queryFn: async () => {
      const url = selectedCustomer ? `/api/lookup/sponsors?customer_id=${selectedCustomer.customer_id}` : '/api/lookup/sponsors';
      const { data } = await axiosClient.get(url);
      return data;
    },
    enabled: open,
  });

  // Wipe every piece of wizard state back to initial. Called both on
  // dialog open (fresh session) and on close (so the next open starts
  // clean even if the parent leaves <Dialog/> mounted).
  const resetWizardState = () => {
    setMode('choose');
    setForm(freshForm(auditUser));
    setActiveStep(0);
    setTenantAttr({});
    setTenantOption({});
    setAuthConfig({});
    setImportResult(null);
    setImportedFiles({});
    setParsedImportData({});
    setTenantCodeSource('generated');
    setSelectedCustomer(null);
    setSelectedSponsor(null);
    setZipCustomerCode(null);
    setZipSponsorCode(null);
    setDefaultTemplates([]);
    setSelectedTemplates(new Set());
    setLoadingTemplates(false);
    setCreatedSummary(null);
  };

  // Reset form and generate fresh codes every time wizard opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    resetWizardState();
    axiosClient.get('/api/lookup/next-tenant-id')
      .then(({ data }) => {
        if (cancelled) return;
        setForm((p) => ({ ...p, tenant_id: data.next_id }));
      })
      .catch(() => { /* lookup unavailable — keep the auto-generated tenant code */ });
    return () => { cancelled = true; };
    // resetWizardState is recreated each render but only the open
    // transition matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

   useEffect(() => {
    if (Object.keys(importedFiles).length === 0) return;
    let cancelled = false;
    axiosClient.get('/api/lookup/next-tenant-id')
      .then(({ data }) => {
        if (cancelled) return;
        setForm((p) => ({ ...p, tenant_id: data.next_id }));
      })
      .catch(() => { /* lookup unavailable — keep the auto-generated tenant code */ });
    return () => { cancelled = true; };
  }, [importedFiles]);

  // Determine if the current step is the HTML Templates step. Match by
  // label so reordering MANUAL_STEPS / IMPORT_STEPS doesn't silently
  // break the templates-loading effect.
  const STEPS = mode === 'import' ? IMPORT_STEPS : MANUAL_STEPS;
  const isHtmlTemplatesStep = mode !== 'choose' && STEPS[activeStep]?.label === HTML_TEMPLATES_LABEL;

  // Fetch default HTML templates when the HTML Templates step is reached
  useEffect(() => {
    if (!isHtmlTemplatesStep || defaultTemplates.length > 0) return;
    let cancelled = false;
    setLoadingTemplates(true);
    axiosClient.get(`/api/html-template/defaults?env=${htmlTemplatesEnv}`)
      .then(({ data }) => {
        if (cancelled) return;
        const rawFiles = Array.isArray(data) ? data : Array.isArray(data?.files) ? data.files : [];
        const templates: Array<{ key: string; language: string; fileName: string }> = rawFiles.map((item: any) => ({
          key: item.key || item.Key || `${item.language || item.Language}/${item.fileName || item.FileName}`,
          language: item.language || item.Language || 'en-US',
          fileName: item.fileName || item.FileName || item.file_name || item.key || '',
        }));
        setDefaultTemplates(templates);
        setSelectedTemplates(new Set(templates.map((t) => t.key)));
      })
      .catch(() => {
        setSnackbar({ open: true, severity: 'error', message: 'Failed to load default HTML templates' });
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => { cancelled = true; };
  }, [isHtmlTemplatesStep, htmlTemplatesEnv]); // eslint-disable-line react-hooks/exhaustive-deps

  // When customer changes, auto-populate customer_code and reset sponsor
  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setSelectedSponsor(null);
    setForm((p) => ({
      ...p,
      customer_code: customer?.customer_code || '',
    }));
  };

  useEffect(() => {
    const code = initialCustomerCode || zipCustomerCode;
    if (!open || !code || customers.length === 0 || selectedCustomer) return;
    const customer = customers.find((item) => item.customer_code === code);
    if (customer) handleCustomerChange(customer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCustomerCode, zipCustomerCode, customers, selectedCustomer]);

  // When sponsor changes, auto-populate sponsor_id and sponsor_code
  const handleSponsorChange = (sponsor: Sponsor | null) => {
    setSelectedSponsor(sponsor);
    setForm((p) => ({
      ...p,
      sponsor_id: sponsor ? parseInt(sponsor.sponsor_id, 10) : undefined,
      sponsor_code: sponsor?.sponsor_code || '',
    }));
  };

  useEffect(() => {
    const code = initialSponsorCode || zipSponsorCode;
    if (!open || !code || sponsors.length === 0 || selectedSponsor) return;
    const sponsor = sponsors.find((item) => item.sponsor_code === code);
    if (sponsor) handleSponsorChange(sponsor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSponsorCode, zipSponsorCode, sponsors, selectedSponsor]);

  const updateField = (field: string, value: unknown) => setForm((p) => ({ ...p, [field]: value }));

  // Copy the user's selected default HTML templates onto the new tenant.
  // Used by both manual and import branches; pulled out to keep
  // mutationFn focused on the create/import API call.
  const copySelectedTemplates = async (tenantCode: string) => {
    if (selectedTemplates.size === 0) return;
    await axiosClient.post('/api/html-template/copy-defaults', {
      env: htmlTemplatesEnv,
      tenantCode,
      files: Array.from(selectedTemplates),
    });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      // Use parsedImportData.tenant rather than `Object.keys(...).length > 0`
      // so a single-file import with only e.g. cms.json doesn't accidentally
      // hit the import endpoint without a tenant payload.
      if (mode === 'import' && parsedImportData.tenant) {
        // Import mode: one stored procedure call creates tenant + all entities in config.* tables.
        // Merge user's form edits into the tenant data from the ZIP. The ZIP shape is
        // { tenant: {...} } where the inner object is the actual tenant row, hence the nested spread.
        const importedTenant = parsedImportData.tenant as Record<string, unknown>;
        const importedTenantInner = (importedTenant.tenant ?? {}) as Record<string, unknown>;
        const editedTenant = {
          ...importedTenant,
          tenant: {
            ...importedTenantInner,
            tenantName: form.tenant_name,
            tenantId: form.tenant_id,
            tenant_id: form.tenant_id,
            tenantCode: form.tenant_code,
            planYear: form.plan_year,
            apiKey: form.api_key,
            partnerCode: form.partner_code,
            sponsorId: form.sponsor_id,
            sponsorCode: form.sponsor_code,
            customerCode: form.customer_code,
            selfReport: form.self_report,
            directLogin: form.direct_login,
            enableServerLogin: form.enable_server_login,
            utcTimeOffset: form.utc_time_offset,
            dstEnabled: form.dst_enabled,
            encKeyId: form.enc_key_id,
            tenantAttr: tenantAttr,
            tenantOptionJson: tenantOption,
            authConfig: authConfig,
          },
        };

        const { data } = await axiosClient.post(`/api/tenant/${form.tenant_code}/import`, {
          importOptions: ['ALL'],
          createdBy: auditUser,
          tenantData: editedTenant,
          // metadata omitted — stored proc auto-creates needed types from task/CMS data
          taskData: parsedImportData.task ?? null,
          cmsData: parsedImportData.cms ?? null,
          fisData: parsedImportData.fis ?? null,
          cohortData: parsedImportData.cohort ?? null,
          sweepstakesData: parsedImportData.sweepstakes ?? null,
          walletData: parsedImportData.wallet ?? null,
          adventureData: parsedImportData.adventure ?? null,
          collectionData: parsedImportData.taskrewardcollection ?? null,
          adminData: parsedImportData.admin ?? null,
          // Flex Components (colors, palette, attributes, block library) — config-
          // side draft only; flex_v1.* platform tables come from a subsequent
          // publish, same as Task V2 actions. Previously omitted entirely, so a
          // ZIP-imported tenant always lost Flex config and showed "Open in
          // Consumer App" instead of "Open in Flex Consumer App" for its users.
          colorData: parsedImportData.color ?? null,
          // CPT-1307: Task V2 actions/catalog and Earn Milestones (lifecycle
          // rules) were exported but never sent to import — a ZIP-cloned
          // tenant silently lost both. Config-side draft only, same as color
          // above; platform tables are hydrated by the subsequent publish.
          taskV2Data: parsedImportData.taskv2 ?? null,
          lifecycleData: parsedImportData.lifecycle ?? null,
          // PLT-3837: the FLEX app itself (flex_v1 pages, components, layouts).
          // colorData above only carries paint; without this the tenant had
          // no FLEX screens.
          flexAppData: parsedImportData.flexapp ?? null,
          // PLT-3837: the tenant's notification rules, messages, categories
          // and channels (tenantnotification.json).
          notificationData: parsedImportData.tenantnotification ?? null,
          // contentData intentionally omitted — the wizard handles content in a
          // separate /import/content call below (which creates Storyblok stories).
          // Passing it here would cause syncContentMirror to pre-insert content.*
          // DB rows, which then causes importContentJson to skip all Storyblok
          // creation (the existingItem guard fires for every item).
        });
        await copySelectedTemplates(form.tenant_code);

        // Auto-publish to the platform so tenant_cohort → cohort.cohort,
        // tenant_adventure → task.adventure, etc. are hydrated. Without
        // this, consumer-bff endpoints return 404 (no adventures/cohorts
        // found) because platform tables stay empty until the user runs
        // Publish manually. A fresh clone should just work end-to-end.
        let publishResult: { status?: string; entityCounts?: Record<string, number>; error?: string; detail?: unknown } | null = null;
        try {
          const { data: pub } = await axiosClient.post(`/api/publish/${form.tenant_code}`, {
            createdBy: auditUser,
            options: ['ALL'],
          });
          publishResult = pub;
        } catch (pubErr) {
          // Non-fatal: tenant exists in config.*; the user can retry publish
          // from the Publish tab. Bubble the error text up so the snackbar
          // can surface it.
          const err = pubErr as { response?: { data?: { error?: string } & Record<string, unknown> }; message?: string };
          publishResult = {
            status: 'failed',
            error: err?.response?.data?.error || err?.message || 'Auto-publish failed',
            detail: err?.response?.data,
          };
        }

        // Push questionnaire / question / question_group rows into the platform
        // schema. The PG tenant_import proc only writes the config-side mirror
        // (questionnaire_tenant_binding); without this call task.questionnaire
        // stays empty and re-exports come back with no questions. Skipped on
        // publish failure since task_reward FKs won't exist yet. Non-fatal —
        // tenant is still usable, questionnaires just need a manual sync.
        if (publishResult && !publishResult.error && parsedImportData?.task) {
          try {
            await axiosClient.post(
              `/api/tenant/${form.tenant_code}/import/questionnaire-platform-sync`,
              { taskData: parsedImportData.task, createdBy: auditUser },
            );
          } catch (qErr) {
            // eslint-disable-next-line no-console
            console.error('Questionnaire platform sync failed (non-fatal):', qErr);
          }
        }

        let contentResult: {
          status?: string;
          counts?: Record<string, number>;
          errors?: string[];
          warnings?: string[];
          linkedCohorts?: string[];
        } | null = null;
        if (publishResult && !publishResult.error && parsedImportData?.content) {
          // Content import is async: POST returns 202 + job_id and the
          // background worker calls Storyblok at 4 req/sec for what can
          // easily be 60–180s of wall time. We can't keep a single HTTP
          // request open that long under Cloudflare's 100s proxy cap, so
          // we poll the job status endpoint until it terminates.
          const POLL_INTERVAL_MS = 3000;
          const POLL_TIMEOUT_MS = 15 * 60 * 1000; // 15 min — well beyond worst case
          // A failed status check is judged by createPollMissTracker: the job
          // lives on the API server that started it, and on an env with
          // several servers (qa, uat, integ, prod) a check that lands
          // elsewhere answers 404 job_not_found while the job runs fine.
          // Giving up there made the wizard republish before the content job
          // had restored the Task V2 story refs (PLT-3837 QA run 2026-10-26).
          try {
            const { data: enqueue } = await axiosClient.post<{ job_id: string; status: string }>(
              `/api/tenant/${form.tenant_code}/import/content`,
              {
                contentData: parsedImportData.content,
                // The Task V2 section carries the source tenant's story refs; the server
                // rewrites them to the stories this import creates.
                taskV2Data: parsedImportData.taskv2 ?? null,
                createdBy: auditUser,
                createStories: tenantCodeSource === 'zip' ? createContentStories : true,
              },
            );
            const jobId = enqueue.job_id;
            const startTime = Date.now();
            const contentMisses = createPollMissTracker();
            // Sleep first — even tiny imports take a few seconds, so an
            // immediate GET would always be 'running'.
            while (true) {
              await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
              if (Date.now() - startTime > POLL_TIMEOUT_MS) {
                contentResult = {
                  status: 'failed',
                  counts: {},
                  errors: [`Content import is still running after ${POLL_TIMEOUT_MS / 60000} minutes; the wizard has stopped polling. The job may still complete in the background — refresh the tenant view to verify.`],
                  warnings: [],
                  linkedCohorts: [],
                };
                break;
              }
              let status: {
                status: 'running' | 'done' | 'error';
                result?: typeof contentResult;
                error?: string;
                progress?: { phase: string; done: number; total: number } | null;
              };
              try {
                const resp = await axiosClient.get<typeof status>(
                  `/api/tenant/${form.tenant_code}/import/content/${jobId}`,
                );
                status = resp.data;
                contentMisses.reached();
                if (status.status === 'running' && status.progress && status.progress.total > 0) {
                  setContentProgress(
                    `Creating content \u2014 ${status.progress.done}/${status.progress.total} (${status.progress.phase})`,
                  );
                }
              } catch (pollErr) {
                const miss = contentMisses.missed(pollErr);
                if (miss.giveUp) {
                  contentResult = {
                    status: 'failed',
                    counts: {},
                    errors: [`${lostJobMessage('content import', miss)} Refresh the tenant view to verify.`],
                    warnings: [],
                    linkedCohorts: [],
                  };
                  break;
                }
                continue; // another server, or a transient blip — try again next tick
              }
              if (status.status === 'running') continue;
              if (status.status === 'done') { contentResult = status.result ?? null; break; }
              // status === 'error'
              contentResult = {
                status: 'failed',
                counts: {},
                errors: [status.error || 'Content import failed in background'],
                warnings: [],
                linkedCohorts: [],
              };
              break;
            }
          } catch (contentErr) {
            const err = contentErr as { response?: { data?: { error?: string; message?: string } }; message?: string };
            contentResult = {
              status: 'failed',
              counts: {},
              errors: [err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Content import failed'],
              warnings: [],
              linkedCohorts: [],
            };
          }
        }

        // The content import re-applied the Task V2 refs against the new stories; publish
        // again so they reach the platform tables (the first publish ran before they existed).
        if (parsedImportData?.taskv2 && contentResult && contentResult.status !== 'failed' && !publishResult?.error) {
          try {
            await axiosClient.post(`/api/publish/${form.tenant_code}`, { createdBy: auditUser, options: ['task_v2'] });
          } catch (_) {
            // Non-fatal: the Publish tab can be run manually.
          }
        }

        // PLT-3837: a publish stamps the onboarding flow step and the V1 survey
        // reward with this tenant's survey catalog in config AFTER its proc has
        // copied config to the platform, so those stamps only reach the platform
        // on the NEXT publish. Without this one the new tenant's onboarding
        // survey step had no catalog on the platform (and its V1 reward the
        // source tenant's) until someone pressed Publish.
        if (!publishResult?.error) {
          try {
            await axiosClient.post(`/api/publish/${form.tenant_code}`, { createdBy: auditUser, options: ['ALL'] });
          } catch (_) {
            // Non-fatal: the Publish tab can be run manually.
          }
        }

        setContentProgress(null);
        return { tenant_code: form.tenant_code, ...data, publish: publishResult, content: contentResult };
      }

      // Manual mode: simple create with form data only.
      const payload: TenantCreatePayload & {
        tenant_attr: Record<string, unknown>;
        tenant_option_json: Record<string, unknown>;
        auth_config: Record<string, unknown>;
        change_summary: string;
      } = {
        ...form,
        tenant_attr: tenantAttr,
        tenant_option_json: tenantOption,
        auth_config: authConfig,
        change_summary: 'Tenant created via wizard',
      };
      const result = await tenantApi.create(payload);

      await copySelectedTemplates(result.tenant_code);

      return result;
    },
    onSuccess: (created: any) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      // Import-mode success: show the summary screen first. Manual-mode
      // success has no entity counts to report, go straight to dashboard.
      const isImport = mode === 'import' && created?.entityCounts;
      if (isImport) {
        setCreatedSummary({
          tenantCode: created.tenant_code,
          entityCounts: created.entityCounts ?? {},
          errors: Array.isArray(created.errors) ? created.errors : [],
          warnings: Array.isArray(created.warnings) ? created.warnings : [],
          danglingGlobalRefs: Array.isArray(created.danglingGlobalRefs) ? created.danglingGlobalRefs : [],
          status: created.status ?? 'success',
          publish: created.publish ?? null,
          content: created.content ?? null,
        });
      } else {
        onCreated(created.tenant_code);
      }
    },
    onError: (err: any) => {
      const message = err?.response?.data?.error || 'Failed to create tenant. Please try again.';
      setSnackbar({ open: true, severity: 'error', message });
    },
  });

  // FILE_KEY_MAP used for single-file import result display

  // Populate wizard form from tenant.json data
  const populateFormFromTenant = (json: any) => {
    const t = json?.data?.tenant || json?.Data?.Tenant || json?.tenant;
    if (!t) return;

    setForm((prev) => ({
      ...prev,
      tenant_name: t.tenantName || t.TenantName || prev.tenant_name,
      sponsor_id: t.sponsorId || t.SponsorId || prev.sponsor_id,
      plan_year: t.planYear || t.PlanYear || prev.plan_year,
      // PLT-3837: a new tenant gets its own API key. Taking the ZIP's key made
      // the copy share the source tenant's key on config and platform.
      api_key: prev.api_key || safeUUID().replace(/-/g, ''),
      self_report: t.selfReport ?? t.SelfReport ?? false,
      direct_login: t.directLogin ?? t.DirectLogin ?? false,
      enable_server_login: t.enableServerLogin ?? t.EnableServerLogin ?? false,
      utc_time_offset: t.utcTimeOffset || t.UtcTimeOffset || '',
      dst_enabled: t.dstEnabled ?? t.DstEnabled ?? false,
      enc_key_id: t.encKeyId || t.EncKeyId || '',
      redemption_vendor_name_0: t.redemption_vendor_name_0 || t.RedemptionVendorName0 || '',
      redemption_vendor_partner_id_0: t.redemption_vendor_partner_id_0 || t.RedemptionVendorPartnerId0 || '',
    }));

    setZipCustomerCode(t.customerCode || t.CustomerCode || null);
    setZipSponsorCode(t.sponsorCode || t.SponsorCode || null);

    let attr = t.tenantAttribute || t.TenantAttribute || t.tenantAttr;
    if (typeof attr === 'string') try { attr = JSON.parse(attr); } catch { attr = {}; }
    setTenantAttr(attr || {});

    let opt = t.tenantOption || t.TenantOption || t.tenantOptionJson;
    if (typeof opt === 'string') try { opt = JSON.parse(opt); } catch { opt = {}; }
    setTenantOption(opt || {});

    let auth = t.authConfig || t.AuthConfig;
    if (typeof auth === 'string') try { auth = JSON.parse(auth); } catch { auth = {}; }
    setAuthConfig(auth || {});
  };

  // Reuse the ZIP's source tenant code instead of the auto-generated one IFF
  // it's free on this platform (absent from both tenant.tenant and
  // config.tenant). Keeps identifiers stable across env promotion. Anything
  // ambiguous — no source code, code already taken, or a lookup error — keeps
  // the generated code (the safe default). `sourceTenantObj` is the parsed
  // tenant.json. The real export wraps it as { data: { tenant: {...} } } (see
  // getTenantExport), and single-file uploads may arrive as { Data: { Tenant } }
  // or a raw { tenant } / row — so unwrap all of these, exactly as
  // populateFormFromTenant does. (Bug fix: the prior `?.tenant ?? obj` only
  // handled { tenant }, so real ZIP exports yielded an empty code and always
  // fell back to a generated one even when the source code was free.)
  const applyZipTenantCode = async (sourceTenantObj: any) => {
    const inner = sourceTenantObj?.data?.tenant
      ?? sourceTenantObj?.Data?.Tenant
      ?? sourceTenantObj?.tenant
      ?? sourceTenantObj
      ?? {};
    const zipCode = String(inner.tenantCode || inner.tenant_code || '').trim();
    if (!zipCode) { setTenantCodeSource('generated'); return; }
    try {
      const { available } = await tenantApi.codeAvailability(zipCode);
      if (available) {
        setForm((p) => ({ ...p, tenant_code: zipCode }));
        setTenantCodeSource('zip');
      } else {
        setTenantCodeSource('taken'); // source code already on this platform
      }
    } catch {
      setTenantCodeSource('generated'); // fail-safe: keep the generated code
    }
  };

  // Handle Import All — ZIP or single tenant.json
  const handleImportAll = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    setImporting(true);
    setImportResult(null);
    setImportedFiles({});

    try {
      if (file.name.endsWith('.zip')) {
        // Parse ZIP — extract all JSON files using shared utility
        const { parseExportZip } = await import('../../utils/zipParser');
        const { parsedFiles, fileList } = await parseExportZip(file);

        // Populate wizard form from tenant data (no API call — just parse)
        if (parsedFiles.tenant) populateFormFromTenant({ data: parsedFiles.tenant });

        // Reuse the ZIP's tenant code if it's free on this platform.
        if (parsedFiles.tenant) await applyZipTenantCode(parsedFiles.tenant);

        // Store parsed data for later use during create
        setParsedImportData(parsedFiles);

        const results: Record<string, string> = { 'Files parsed': fileList.join(', ') };
        Object.entries(parsedFiles).forEach(([k, v]) => {
          if (Array.isArray(v)) results[k] = `${v.length} items`;
          else if (v && typeof v === 'object') results[k] = 'Loaded';
        });
        setImportResult(results);
        setImportedFiles(Object.fromEntries(fileList.map(f => [FILE_KEY_MAP[f.toLowerCase()] || f, 'Parsed'])));
        setSnackbar({ open: true, severity: 'success', message: `Parsed ${fileList.length} files. Review each step, then create.` });

      } else {
        // Single JSON file — treat as tenant.json. Populate both the
        // form (so review steps render) and parsedImportData (so the
        // create mutation takes the import branch and hits
        // /api/tenant/:code/import instead of the manual create endpoint).
        const text = await file.text();
        const json = JSON.parse(text);
        const tenantPayload = (json.Data || json.data || json) as ImportFilePayload;
        populateFormFromTenant(json);
        await applyZipTenantCode(tenantPayload);
        setParsedImportData({ tenant: tenantPayload });
        setImportedFiles({ tenant: 'Imported' });
        setImportResult({ 'tenant.json': 'Loaded — review settings in previous steps' });
        setSnackbar({ open: true, severity: 'success', message: 'Imported tenant.json! Review each step before creating.' });
        setActiveStep(0);
      }
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: `Import failed: ${err.message || 'Invalid file'}` });
    } finally {
      setImporting(false);
    }
  };

  // Handle individual JSON file import
  const handleIndividualImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const key = individualFileKey.current;
    if (!file || !key) return;
    event.target.value = '';

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const data = json.Data || json.data || json;

      // If it's tenant.json, also populate the wizard form
      if (key === 'tenant') {
        populateFormFromTenant(json);
      }

      // Store parsed data for import on tenant creation (same as ZIP flow)
      setParsedImportData((prev) => ({ ...prev, [key]: data }));
      setImportedFiles((prev) => ({ ...prev, [key]: 'Parsed — will import on tenant creation' }));
      setSnackbar({ open: true, severity: 'success', message: `${key}.json parsed and ready. It will be imported when the tenant is created.` });
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: `Failed to import ${key}.json: ${err.message}` });
    }
  };

  // Basic Info is step 0 in manual mode, step 1 in import mode (step 0 there
  // is the file-upload step) — matches isStepValid()'s step numbering above.
  const isBasicInfoStep = (mode === 'manual' && activeStep === 0) || (mode === 'import' && activeStep === 1);

  const handleNext = async () => {
    if (isBasicInfoStep && form.tenant_name.trim() && form.sponsor_code) {
      setCheckingTenantName(true);
      try {
        const { data } = await axiosClient.get('/api/tenant/name-availability', {
          params: { name: form.tenant_name.trim(), sponsor_code: form.sponsor_code },
        });
        if (!data.available) {
          setTenantNameError('A tenant with this name already exists for this sponsor');
          return;
        }
        setTenantNameError(null);
      } catch {
        // Availability check unreachable — don't block the wizard on it;
        // the create-time check on the backend still guards the actual insert.
        setTenantNameError(null);
      } finally {
        setCheckingTenantName(false);
      }
    }

    if (activeStep === STEPS.length - 1) {
      createMutation.mutate();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const handleClose = () => {
    // If a summary is being shown, dismissing the dialog should still
    // route the user to the newly-created tenant's dashboard rather
    // than drop them back on the SelectTenant list without context.
    if (createdSummary) {
      const tc = createdSummary.tenantCode;
      setCreatedSummary(null);
      onCreated(tc);
      return;
    }
    resetWizardState();
    onClose();
  };

  const isStepValid = () => {
    if (mode === 'manual' && activeStep === 0) return !!selectedCustomer && !!selectedSponsor && !!form.tenant_name;
    if (mode === 'import' && activeStep === 0) return Object.keys(importedFiles).length > 0;
    if (mode === 'import' && activeStep === 1) return !!selectedCustomer && !!selectedSponsor && !!form.tenant_name;
    return true;
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reused by both the summary path (early return) and the wizard path so
  // toast feedback works regardless of which dialog body is rendered.
  const snackbarEl = (
    <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
    </Snackbar>
  );

  // Post-import summary: renders entity counts, errors, warnings, publish
  // result, and dangling globals. Returned via early-return below so the
  // wizard render path doesn't need to wrap every section in
  // `{!createdSummary && ...}` guards.
  if (createdSummary) {
    const headerBg = createdSummary.errors.length > 0 || createdSummary.status === 'failed'
      ? colors.errorBgStrong
      : createdSummary.status === 'partial' || createdSummary.warnings.length > 0 || createdSummary.danglingGlobalRefs.length > 0
        ? colors.warningBgStrong
        : colors.successBgStrong;
    const headerIcon = createdSummary.errors.length > 0 || createdSummary.status === 'failed'
      ? '#B91C1C'
      : createdSummary.status === 'partial' || createdSummary.warnings.length > 0 || createdSummary.danglingGlobalRefs.length > 0
        ? '#B45309'
        : colors.successText;
    return (
      <>
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper"
          PaperProps={{ sx: { borderRadius: '16px', minHeight: '80vh' } }}>
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, px: 4, py: 4, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: '50%',
                  backgroundColor: headerBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: headerIcon }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                    Tenant created
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.25 }}>
                    {createdSummary.tenantCode}
                  </Typography>
                </Box>
              </Box>

              {/* Inserted entity counts */}
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5, fontFamily: 'Manrope, sans-serif' }}>
                  Inserted
                </Typography>
                {Object.keys(createdSummary.entityCounts).length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    No entities were inserted (tenant metadata only).
                  </Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
                    {Object.entries(createdSummary.entityCounts)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([entity, count]) => (
                        <Paper key={entity} elevation={0} sx={{
                          p: 1.5, borderRadius: '10px', border: `1px solid ${colors.borderLight}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                          <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{entity}</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#3E8C1C' }}>{count}</Typography>
                        </Paper>
                      ))}
                  </Box>
                )}
              </Box>

              {/* Errors */}
              {createdSummary.errors.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5, color: '#B91C1C', fontFamily: 'Manrope, sans-serif' }}>
                    Errors ({createdSummary.errors.length})
                  </Typography>
                  <Paper elevation={0} sx={{
                    p: 2, borderRadius: '10px', border: `1px solid ${colors.errorBorder}`, backgroundColor: colors.errorBg,
                  }}>
                    {createdSummary.errors.map((msg, i) => (
                      <Typography key={i} sx={{ fontSize: 12, fontFamily: 'monospace', color: colors.errorText, mb: i < createdSummary.errors.length - 1 ? 0.5 : 0 }}>
                        {msg}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}

              {/* Warnings (includes dangling-ref summary) */}
              {createdSummary.warnings.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5, color: '#B45309', fontFamily: 'Manrope, sans-serif' }}>
                    Warnings
                  </Typography>
                  <Paper elevation={0} sx={{
                    p: 2, borderRadius: '10px', border: `1px solid ${colors.warningBorder}`, backgroundColor: colors.warningBg,
                  }}>
                    {createdSummary.warnings.map((msg, i) => (
                      <Typography key={i} sx={{ fontSize: 12, color: colors.warningText, mb: i < createdSummary.warnings.length - 1 ? 0.75 : 0 }}>
                        {msg}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}

              {/* Auto-publish result: whether the tenant was pushed to the
                  platform tables so the consumer BFF can see it. A freshly
                  imported tenant without this step returns 404 for
                  adventures / cohorts / tasks because task.*, cohort.*,
                  etc. stay empty until publish runs. */}
              {createdSummary.publish && (
                <Box>
                  <Typography sx={{
                    fontSize: 14, fontWeight: 700, mb: 1.5,
                    color: createdSummary.publish.error ? '#B91C1C' : colors.successText,
                    fontFamily: 'Manrope, sans-serif',
                  }}>
                    Platform publish ({createdSummary.publish.status || (createdSummary.publish.error ? 'failed' : 'success')})
                  </Typography>
                  {createdSummary.publish.error ? (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '10px', border: `1px solid ${colors.errorBorder}`, backgroundColor: colors.errorBg }}>
                      <Typography sx={{ fontSize: 12, color: colors.errorText, fontFamily: 'monospace' }}>
                        {createdSummary.publish.error}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: colors.textSecondary, mt: 1 }}>
                        Tenant config was saved but not pushed to the platform. Retry from the Publish tab.
                      </Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {Object.entries(createdSummary.publish.entityCounts || {}).filter(([, v]) => v).map(([k, v]) => (
                        <Chip
                          key={k}
                          label={`${k}: ${String(v)}`}
                          size="small"
                          sx={{ fontSize: 11, fontFamily: 'monospace', backgroundColor: colors.successBgStrong, color: colors.successText }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {createdSummary.content && (() => {
                const cContent = createdSummary.content;
                return (
                  <Box>
                    <Typography sx={{
                      fontSize: 14, fontWeight: 700, mb: 1.5,
                      color: colors.successText,
                      fontFamily: 'Manrope, sans-serif',
                    }}>
                      Content (Storyblok)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {Object.entries(cContent.counts || {}).filter(([, v]) => v).map(([k, v]) => (
                        <Chip
                          key={k}
                          label={`${k}: ${String(v)}`}
                          size="small"
                          sx={{ fontSize: 11, fontFamily: 'monospace', backgroundColor: colors.successBgStrong, color: colors.successText }}
                        />
                      ))}
                      {(cContent.linkedCohorts?.length ?? 0) > 0 && (
                        <Chip
                          label={`linked_cohorts: ${cContent.linkedCohorts?.length ?? 0}`}
                          size="small"
                          sx={{ fontSize: 11, fontFamily: 'monospace', backgroundColor: colors.successBgStrong, color: colors.successText }}
                        />
                      )}
                    </Box>
                    {/* Content errors/warnings were previously dropped here, so the
                        wizard said "created" even when every item was skipped.
                        Surface them so a partial content import is never silent. */}
                    {(cContent.errors?.length ?? 0) > 0 && (
                      <Paper elevation={0} sx={{ mt: 1.5, p: 2, borderRadius: '10px', border: `1px solid ${colors.warningBorder}`, backgroundColor: colors.warningBg, maxHeight: 200, overflow: 'auto' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.warningText, mb: 0.5 }}>
                          {cContent.errors?.length ?? 0} content item(s)/step(s) did not import:
                        </Typography>
                        {(cContent.errors ?? []).map((e: string, i: number) => (
                          <Typography key={i} sx={{ fontSize: 11, fontFamily: 'monospace', color: colors.warningText, mb: 0.25 }}>{e}</Typography>
                        ))}
                      </Paper>
                    )}
                    {(cContent.warnings?.length ?? 0) > 0 && (
                      <Typography sx={{ mt: 1, fontSize: 11, color: colors.textSecondary }}>
                        {cContent.warnings?.length ?? 0} warning(s): {(cContent.warnings ?? []).slice(0, 3).join(' · ')}{(cContent.warnings?.length ?? 0) > 3 ? ' …' : ''}
                      </Typography>
                    )}
                  </Box>
                );
              })()}

              {/* Dangling global references (unresolved globals referenced by the import) */}
              {createdSummary.danglingGlobalRefs.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5, color: '#B45309', fontFamily: 'Manrope, sans-serif' }}>
                    Missing global references ({createdSummary.danglingGlobalRefs.length})
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 1 }}>
                    These codes were referenced by the import but don&apos;t resolve to an active global.
                    Publish will fail until a platform admin creates them or the references are removed.
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: '10px', border: `1px solid ${colors.warningBorder}`, backgroundColor: colors.warningBg, maxHeight: 220, overflow: 'auto' }}>
                    {createdSummary.danglingGlobalRefs.map((r, i) => (
                      <Typography key={i} sx={{ fontSize: 12, fontFamily: 'monospace', color: colors.warningText, mb: 0.25 }}>
                        {r.kind}: {r.code}{r.qualifier ? ` (${r.qualifier})` : ''}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 4, py: 2, borderTop: `1px solid ${colors.borderLight}`, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => {
                const tc = createdSummary.tenantCode;
                setCreatedSummary(null);
                onCreated(tc);
              }}
              sx={{
                textTransform: 'none', backgroundColor: '#3E8C1C', color: colors.bgWhite,
                fontWeight: 600, borderRadius: '8px', px: 3,
                '&:hover': { backgroundColor: '#2E6B14' },
              }}
            >
              Go to Dashboard
            </Button>
          </DialogActions>
        </Dialog>
        {snackbarEl}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper"
        PaperProps={{ sx: { borderRadius: '16px', minHeight: '80vh' } }}>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ px: 4, pt: 3, pb: 2, borderBottom: `1px solid ${colors.borderLight}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {mode !== 'choose' && (
                <Button size="small" onClick={() => { setMode('choose'); setActiveStep(0); }}
                  sx={{ minWidth: 0, p: 0.5, color: colors.textSecondary, '&:hover': { color: colors.textPrimary } }}>
                  <ArrowBackIcon sx={{ fontSize: 18 }} />
                </Button>
              )}
              <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif', mb: 0.5 }}>
                  {mode === 'choose' ? 'Create New Tenant' : mode === 'import' ? 'Import Tenant' : 'New Tenant Setup'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                  {mode === 'choose' ? 'How would you like to set up this tenant?' : mode === 'import' ? 'Upload and review your tenant configuration' : 'Configure your tenant step by step'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Mode chooser */}
          {mode === 'choose' && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 4, py: 6 }}>
              <Box sx={{ display: 'flex', gap: 3, maxWidth: 700, width: '100%' }}>
                <Paper elevation={0} onClick={() => setMode('import')}
                  sx={{
                    flex: 1, p: 4, borderRadius: '16px', border: `2px solid ${colors.borderLight}`, cursor: 'pointer',
                    textAlign: 'center', transition: 'all 0.2s',
                    '&:hover': { borderColor: colors.brand, boxShadow: '0 4px 20px rgba(250,179,6,0.15)' },
                  }}>
                  <CloudUploadIcon sx={{ fontSize: 48, color: colors.brand, mb: 2 }} />
                  <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Manrope, sans-serif', mb: 1 }}>
                    Import from ZIP
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
                    Upload a tenant export ZIP or individual JSON files. Best when cloning an existing tenant or migrating between environments.
                  </Typography>
                </Paper>
                <Paper elevation={0} onClick={() => setMode('manual')}
                  sx={{
                    flex: 1, p: 4, borderRadius: '16px', border: `2px solid ${colors.borderLight}`, cursor: 'pointer',
                    textAlign: 'center', transition: 'all 0.2s',
                    '&:hover': { borderColor: colors.brand, boxShadow: '0 4px 20px rgba(250,179,6,0.15)' },
                  }}>
                  <SaveIcon sx={{ fontSize: 48, color: colors.brand, mb: 2 }} />
                  <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Manrope, sans-serif', mb: 1 }}>
                    Create Manually
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
                    Set up a new tenant from scratch with a guided wizard. Configure identity, attributes, options, and auth step by step.
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}

          {/* Stepper (only when mode is chosen) */}
          {mode !== 'choose' && (
            <Box sx={{ px: 4, py: 2, borderBottom: `1px solid ${colors.borderLight}`, backgroundColor: colors.bgPage }}>
              <Stepper activeStep={activeStep} connector={<GoldConnector />} alternativeLabel>
                {STEPS.map((step, idx) => (
                  <Step key={step.label} completed={idx < activeStep}
                    sx={{ cursor: 'pointer' }} onClick={() => {
                      // Allow free backward navigation; only allow stepping
                      // exactly one step forward, and only if the current
                      // step's required fields are filled.
                      if (idx <= activeStep) setActiveStep(idx);
                      else if (idx === activeStep + 1 && isStepValid()) setActiveStep(idx);
                    }}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          color: idx <= activeStep ? colors.brand : '#D3D6DC',
                          '&.Mui-active': { color: colors.brand },
                          '&.Mui-completed': { color: colors.brand },
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: idx === activeStep ? 700 : 500, color: idx === activeStep ? colors.textPrimary : colors.textSecondary, fontFamily: 'Manrope, sans-serif' }}>
                        {step.label}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: colors.textTertiary }}>{step.description}</Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {/* Step content */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
            {/* Import Mode — Step 0: Upload */}
            {mode === 'import' && activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '2px dashed #D3D6DC', textAlign: 'center', '&:hover': { borderColor: colors.brand } }}>
                  <CloudUploadIcon sx={{ fontSize: 40, color: colors.brand, mb: 1 }} />
                  <Typography sx={{ fontSize: 16, fontWeight: 700, fontFamily: 'Manrope, sans-serif', mb: 0.5 }}>
                    Import Tenant Configuration
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 2 }}>
                    Upload a full tenant export ZIP or a single tenant.json file.
                  </Typography>
                  <input ref={fileInputRef} type="file" accept=".zip,.json" onChange={handleImportAll} style={{ display: 'none' }} />
                  <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    sx={{ textTransform: 'none', backgroundColor: colors.brand, color: colors.textPrimary, fontWeight: 600, borderRadius: '8px', '&:hover': { backgroundColor: '#E5A300' } }}>
                    {importing ? 'Importing...' : 'Choose ZIP or tenant.json'}
                  </Button>
                </Paper>

                <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: `1px solid ${colors.borderLight}` }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'Manrope, sans-serif', mb: 2 }}>
                    Or upload individual files
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    {[
                      { key: 'tenant', label: 'tenant.json', desc: 'Core config, attributes, flows' },
                      { key: 'task', label: 'task.json', desc: 'Actions, rewards, details, T&C' },
                      { key: 'cohort', label: 'cohort.json', desc: 'Cohorts + assignments' },
                      { key: 'cms', label: 'cms.json', desc: 'CMS components' },
                      { key: 'fis', label: 'fis.json', desc: 'FIS account + program config' },
                      { key: 'sweepstakes', label: 'sweepstakes.json', desc: 'Sweepstakes' },
                      { key: 'adventure', label: 'adventure.json', desc: 'Adventures' },
                      { key: 'taskrewardcollection', label: 'taskrewardcollection.json', desc: 'Collections' },
                      { key: 'wallet', label: 'wallet.json', desc: 'Transfer rules' },
                      { key: 'admin', label: 'admin.json', desc: 'Scripts + event handlers' },
                      { key: 'metadata', label: 'metadata.json', desc: 'Lookup tables' },
                      { key: 'content', label: 'content.json', desc: 'Content pages (FAQ/card/agreement)' },
                      { key: 'taskv2', label: 'taskv2.json', desc: 'Task V2 actions + catalog' },
                      { key: 'lifecycle', label: 'lifecycle.json', desc: 'Earn Milestones (lifecycle rules)' },
                    ].map((f) => {
                      const imported = importedFiles[f.key];
                      return (
                        <Box key={f.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: '8px', border: `1px solid ${colors.borderLight}`, backgroundColor: imported ? colors.successBg : colors.bgPage }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: imported ? colors.successText : colors.textPrimary }}>{f.label}</Typography>
                            <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>{imported || f.desc}</Typography>
                          </Box>
                          <Button size="small" variant={imported ? 'text' : 'outlined'}
                            onClick={() => { individualFileKey.current = f.key; individualInputRef.current?.click(); }}
                            sx={{ textTransform: 'none', fontSize: 11, minWidth: 60, borderRadius: '6px', color: imported ? colors.successIcon : colors.textPrimary, borderColor: colors.borderDefault }}>
                            {imported ? 'Done' : 'Upload'}
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                  <input ref={individualInputRef} type="file" accept=".json" onChange={handleIndividualImport} style={{ display: 'none' }} />
                </Paper>

                {importResult && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', backgroundColor: colors.successBgStrong, border: `1px solid ${colors.successBorder}` }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.successText, mb: 0.5 }}>Import Results</Typography>
                    {Object.entries(importResult).map(([k, v]) => (
                      <Typography key={k} sx={{ fontSize: 12, color: colors.successText }}>{k}: {String(v)}</Typography>
                    ))}
                  </Paper>
                )}

                {parsedImportData.content != null && tenantCodeSource === 'zip' && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: `1px solid ${colors.borderLight}` }}>
                    <FormControlLabel
                      control={<Checkbox checked={createContentStories} onChange={(e) => setCreateContentStories(e.target.checked)} size="small" />}
                      label={<Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>Create new Storyblok stories</Typography>}
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                    <Typography sx={{ fontSize: 11, color: colors.textTertiary, ml: 3.75 }}>
                      Checked (default): create fresh Storyblok stories for the imported content. Uncheck to
                      reuse this tenant&apos;s own existing stories (same tenant code as the export, already in this
                      environment&apos;s Storyblok space). Not offered for a different tenant: new tenants always
                      get their own stories.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* Basic Info — Manual step 0 OR Import step 1 */}
            {((mode === 'manual' && activeStep === 0) || (mode === 'import' && activeStep === 1)) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 700 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.brand, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Manrope, sans-serif' }}>Organization</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={customers}
                    value={selectedCustomer}
                    onChange={(_, v) => handleCustomerChange(v)}
                    getOptionLabel={(o) => `${o.customer_name} (${o.customer_code.slice(0, 20)}...)`}
                    renderInput={(params) => <TextField {...params} label="Customer *" size="small" helperText="Select the customer organization" />}
                    isOptionEqualToValue={(o, v) => o.customer_id === v.customer_id}
                  />
                  <WriteButton size="small" onClick={() => setNewCustomerOpen(true)} sx={{ textTransform: 'none', mt: 0.5, whiteSpace: 'nowrap' }}>
                    + New
                  </WriteButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={sponsors}
                    value={selectedSponsor}
                    onChange={(_, v) => handleSponsorChange(v)}
                    getOptionLabel={(o) => `${o.sponsor_name} (${o.sponsor_code.slice(0, 20)}...)`}
                    renderInput={(params) => <TextField {...params} label="Sponsor *" size="small" helperText={selectedCustomer ? `Sponsors under ${selectedCustomer.customer_name}` : 'Select a customer first'} />}
                    isOptionEqualToValue={(o, v) => o.sponsor_id === v.sponsor_id}
                    disabled={!selectedCustomer}
                  />
                  <WriteButton size="small" disabled={!selectedCustomer} onClick={() => setNewSponsorOpen(true)} sx={{ textTransform: 'none', mt: 0.5, whiteSpace: 'nowrap' }}>
                    + New
                  </WriteButton>
                </Box>
                <TextField
                  label="Tenant Name *"
                  size="small"
                  value={form.tenant_name}
                  onChange={(e) => { updateField('tenant_name', e.target.value); setTenantNameError(null); }}
                  error={!!tenantNameError}
                  helperText={tenantNameError || 'Display name for this tenant'}
                />

                <Divider />
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.brand, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Manrope, sans-serif' }}>Auto-Generated</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Tenant Code" size="small" sx={{ flex: 1 }} value={form.tenant_code} InputProps={{ readOnly: true, sx: { fontSize: 13, borderRadius: '8px', backgroundColor: '#f9fafb' } }} helperText={tenantCodeSource === 'zip' ? 'Reusing source tenant code (not on this platform)' : tenantCodeSource === 'taken' ? 'Auto-generated (source code already exists on this platform)' : 'Auto-generated'} />
                  <TextField label="Tenant ID" size="small" sx={{ flex: 1 }} value={form.tenant_id || ''} InputProps={{ readOnly: true, sx: { fontSize: 13, borderRadius: '8px', backgroundColor: '#f9fafb' } }} helperText="Auto-generated" />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Sponsor ID" size="small" sx={{ flex: 1 }} value={form.sponsor_id || ''} InputProps={{ readOnly: true, sx: { fontSize: 13, borderRadius: '8px', backgroundColor: '#f9fafb' } }} helperText="From sponsor" />
                  <TextField label="Sponsor Code" size="small" sx={{ flex: 1 }} value={form.sponsor_code || ''} InputProps={{ readOnly: true, sx: { fontSize: 13, borderRadius: '8px', backgroundColor: '#f9fafb' } }} helperText="From sponsor" />
                  <TextField label="Customer Code" size="small" sx={{ flex: 1 }} value={form.customer_code || ''} InputProps={{ readOnly: true, sx: { fontSize: 13, borderRadius: '8px', backgroundColor: '#f9fafb' } }} helperText="From customer" />
                </Box>

                <Divider />
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.brand, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Manrope, sans-serif' }}>Period & Timezone</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Plan Year" size="small" type="number" sx={{ flex: 1 }} value={form.plan_year} onChange={(e) => updateField('plan_year', parseInt(e.target.value, 10))} />
                  <AppDatePicker label="Period Start" sx={{ flex: 1 }} value={form.period_start_ts || ''} onChange={(v) => updateField('period_start_ts', v)} />
                  <AppDatePicker label="Period End" sx={{ flex: 1 }} value={form.period_end_ts || ''} onChange={(v) => updateField('period_end_ts', v)} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="UTC Time Offset" size="small" sx={{ flex: 1 }} value={form.utc_time_offset || ''} onChange={(e) => updateField('utc_time_offset', e.target.value)} placeholder="UTC-05:00" />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                    <Switch size="small" checked={form.dst_enabled || false} onChange={(e) => updateField('dst_enabled', e.target.checked)} sx={{ '& .Mui-checked': { color: colors.brand }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: colors.brand } }} />
                    <Typography sx={{ fontSize: 13 }}>DST Enabled</Typography>
                  </Box>
                </Box>

                <Divider />
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.brand, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Manrope, sans-serif' }}>Features & API</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {([['self_report', 'Self Report'], ['direct_login', 'Direct Login'], ['enable_server_login', 'Server Login']] as const).map(([field, label]) => (
                    <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch size="small" checked={form[field] || false} onChange={(e) => updateField(field, e.target.checked)} sx={{ '& .Mui-checked': { color: colors.brand }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: colors.brand } }} />
                      <Typography sx={{ fontSize: 13 }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="API Key" size="small" sx={{ flex: 1 }} value={form.api_key || ''} onChange={(e) => updateField('api_key', e.target.value)} />
                  <TextField label="Encryption Key ID" size="small" sx={{ flex: 1 }} value={form.enc_key_id || ''} onChange={(e) => updateField('enc_key_id', e.target.value)} />
                </Box>
                <TextField label="Partner Code" size="small" value={form.partner_code} InputProps={{ readOnly: true, sx: { fontSize: 13, borderRadius: '8px', backgroundColor: '#f9fafb' } }} helperText="Auto-generated (used for mobile login and member import)" />
              </Box>
            )}

            {/* Attributes — Manual step 1 OR Import step 2 */}
            {((mode === 'manual' && activeStep === 1) || (mode === 'import' && activeStep === 2)) && (
              <SchemaFormPanel schema={attrSchema} data={tenantAttr} onChange={setTenantAttr} categories={attrCategories} height="55vh" />
            )}

            {/* Options — Manual step 2 OR Import step 3 */}
            {((mode === 'manual' && activeStep === 2) || (mode === 'import' && activeStep === 3)) && (
              <SchemaFormPanel schema={tenantOptionSchema} data={tenantOption} onChange={setTenantOption} height="55vh" />
            )}

            {/* Auth Config — Manual step 3 OR Import step 4 */}
            {((mode === 'manual' && activeStep === 3) || (mode === 'import' && activeStep === 4)) && (
              <SchemaFormPanel schema={authConfigSchema} data={authConfig} onChange={setAuthConfig} height="55vh" />
            )}

            {/* HTML Templates — Manual step 4 OR Import step 5 */}
            {isHtmlTemplatesStep && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.brand, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Manrope, sans-serif' }}>
                  Select HTML templates to copy into your new tenant
                </Typography>

                {loadingTemplates ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} sx={{ color: colors.brand }} />
                    <Typography sx={{ ml: 2, fontSize: 14, color: colors.textSecondary }}>Loading templates...</Typography>
                  </Box>
                ) : defaultTemplates.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: `1px solid ${colors.borderLight}`, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 14, color: colors.textSecondary }}>No default templates found. You can skip this step.</Typography>
                  </Paper>
                ) : (
                  <>
                    {/* Select all / deselect all + count */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (selectedTemplates.size === defaultTemplates.length) {
                            setSelectedTemplates(new Set());
                          } else {
                            setSelectedTemplates(new Set(defaultTemplates.map((t) => t.key)));
                          }
                        }}
                        sx={{
                          textTransform: 'none', fontSize: 12, fontWeight: 600, borderRadius: '8px',
                          borderColor: '#D3D6DC', color: colors.textPrimary,
                          '&:hover': { borderColor: colors.brand, backgroundColor: '#FAB30608' },
                        }}
                      >
                        {selectedTemplates.size === defaultTemplates.length ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Typography sx={{ fontSize: 13, color: colors.textSecondary, fontWeight: 500 }}>
                        {selectedTemplates.size} of {defaultTemplates.length} templates selected
                      </Typography>
                    </Box>

                    {/* Templates grouped by language */}
                    {Object.entries(
                      defaultTemplates.reduce<Record<string, Array<{ key: string; language: string; fileName: string }>>>((groups, t) => {
                        const lang = t.language || 'Other';
                        if (!groups[lang]) groups[lang] = [];
                        groups[lang].push(t);
                        return groups;
                      }, {})
                    )
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([language, templates]) => (
                        <Paper key={language} elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${colors.borderLight}` }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'Manrope, sans-serif', mb: 1.5, color: colors.textPrimary }}>
                            {language}
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                            {templates.map((t) => (
                              <FormControlLabel
                                key={t.key}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedTemplates.has(t.key)}
                                    onChange={(e) => {
                                      setSelectedTemplates((prev) => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(t.key);
                                        else next.delete(t.key);
                                        return next;
                                      });
                                    }}
                                    sx={{ '&.Mui-checked': { color: colors.brand } }}
                                  />
                                }
                                label={
                                  <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{t.fileName}</Typography>
                                }
                                sx={{ m: 0, py: 0.25 }}
                              />
                            ))}
                          </Box>
                        </Paper>
                      ))}

                    {/* Info note */}
                    <Typography sx={{ fontSize: 12, color: colors.textTertiary, fontStyle: 'italic', mt: 0.5 }}>
                      Selected templates will be copied to cms/html/{'{'}tenantCode{'}'}/ after creation
                    </Typography>
                  </>
                )}
              </Box>
            )}

          </Box>
        </DialogContent>

        {/* Footer with navigation */}
        {mode !== 'choose' && (
          <DialogActions sx={{ px: 4, py: 2, borderTop: `1px solid ${colors.borderLight}`, justifyContent: 'space-between' }}>
            <Button onClick={handleClose} sx={{ textTransform: 'none', color: colors.textSecondary }}>Cancel</Button>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {activeStep > 0 && (
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack}
                  sx={{ textTransform: 'none', color: colors.textPrimary, borderRadius: '8px' }}>
                  Back
                </Button>
              )}
              {mode === 'manual' && activeStep > 0 && (
                <WriteButton
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => createMutation.mutate()}
                  disabled={!form.tenant_name || !selectedCustomer || !selectedSponsor || createMutation.isPending}
                  sx={{
                    textTransform: 'none', borderColor: '#D3D6DC', color: colors.textPrimary,
                    fontWeight: 600, borderRadius: '8px', px: 2,
                    '&:hover': { borderColor: colors.brand, backgroundColor: '#FAB30608' },
                  }}
                >
                  Save & Exit
                </WriteButton>
              )}
              {createMutation.isPending && contentProgress && (
                <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1, color: colors.textSecondary }}>
                  {contentProgress}
                </Typography>
              )}
              <WriteButton
                variant="contained"
                endIcon={activeStep === STEPS.length - 1 ? <CheckCircleIcon /> : <ArrowForwardIcon />}
                onClick={handleNext}
                disabled={!isStepValid() || createMutation.isPending || checkingTenantName}
                sx={{
                  textTransform: 'none', backgroundColor: activeStep === STEPS.length - 1 ? '#3E8C1C' : colors.brand,
                  color: activeStep === STEPS.length - 1 ? colors.bgWhite : colors.textPrimary,
                  fontWeight: 600, borderRadius: '8px', px: 3,
                  '&:hover': { backgroundColor: activeStep === STEPS.length - 1 ? '#2E6B14' : '#E5A300' },
                  '&.Mui-disabled': { backgroundColor: '#E5E5E5', color: colors.textTertiary },
                }}
              >
                {createMutation.isPending || checkingTenantName ? <CircularProgress size={18} sx={{ color: '#FFF' }} /> :
                  activeStep === STEPS.length - 1 ? 'Create Tenant' : 'Next'}
              </WriteButton>
            </Box>
          </DialogActions>
        )}
        {mode === 'choose' && (
          <DialogActions sx={{ px: 4, py: 2, borderTop: `1px solid ${colors.borderLight}` }}>
            <Button onClick={handleClose} sx={{ textTransform: 'none', color: colors.textSecondary }}>Cancel</Button>
          </DialogActions>
        )}
      </Dialog>
      {snackbarEl}
      <CreateCustomerDialog
        open={newCustomerOpen}
        onClose={() => setNewCustomerOpen(false)}
        onCreated={(c) => handleCustomerChange(c)}
      />
      <CreateSponsorDialog
        open={newSponsorOpen}
        onClose={() => setNewSponsorOpen(false)}
        defaultCustomerId={selectedCustomer?.customer_id ?? null}
        lockCustomer
        onCreated={(s) => handleSponsorChange(s)}
      />
    </>
  );
};

export default CreateTenantWizard;
